import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tarjeta del sistema de diseño.
 *
 * En la referencia visual las tarjetas son blancas, con borde hairline gris muy claro,
 * radio de 16px, sombra casi imperceptible y padding de 24px. El layout interior usa
 * `CardHeader` (título + acciones a la derecha) y `CardContent`.
 */

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-card border border-line bg-surface shadow-card', className)}
      {...props}
    />
  );
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Botones/selectores alineados a la derecha del título (ej. "Ver todos", "Últimos 8 días"). */
  actions?: ReactNode;
}

export function CardHeader({ className, children, actions, ...props }: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4 px-6 pt-6', className)} {...props}>
      <div className="min-w-0">{children}</div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn('text-card-title font-semibold text-ink', className)} {...props} />
  );
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('mt-1 text-caption text-ink-muted', className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('border-t border-line px-6 py-4', className)} {...props} />;
}

/**
 * Envoltorio para tablas dentro de una tarjeta: el scroll horizontal queda contenido
 * sin romper el radio de la tarjeta.
 */
export function CardTableArea({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('scroll-thin w-full overflow-x-auto', className)} {...props} />;
}
